import { useCallback, useState } from 'react';

import { useContentPublishCallback } from '@/components/EditorBuilder/Settings/Dialogs/PublishChanges/useContentPublishCallback';
import useSetDialogs from '@/components/EditorBuilder/Settings/hooks/useSetDialogs';
import { Dialogs } from '@/components/EditorBuilder/Settings/types';
import { EditorBuilderActions } from '@/components/EditorBuilder/types';
import useNavigation from '@/hooks/useNavigation';
import useIsPublishable from '@/modules/content/hooks/useIsPublishable';
import routes from '@/pages/corporate/routes';
import { ArticleContentType, PublishContentConfig } from '@/types/schema';
import { getErrorCode } from '@/utils/api/response';

import { REQUIRED_TAG_ERROR_CODE } from '../../tags/utils';
import { EMPTY_ARTICLE_TITLE } from '../consts';
import ArticleEntity from '../enitity';
import { ArticleActions } from '../enums';
import { usePublishArticleMutation } from '../graphql/mutation/generated/PublishArticle';
import { ActionPayloadType } from '../types';

export const TAB_QUERY_KEY = 'tab';

const useArticlePublish = (
  onAction: (type: ArticleActions | EditorBuilderActions, payload?: ActionPayloadType) => void,
  article?: ArticleContentType,
  setDialog?: ReturnType<typeof useSetDialogs>['setDialog'],
) => {
  const [{}, publishArticle] = usePublishArticleMutation();
  const { onSuccess, onError } = useContentPublishCallback();
  const { fetchIsPublishable } = useIsPublishable(article?.id!);
  const [isPublishing, setIsPublishing] = useState(false);
  const navigation = useNavigation();

  const handleTagsError = useCallback(async () => {
    await navigation.replace(routes.ArticleBuilder(article?.id!), {
      query: {
        validationError: 'tag',
      },
      appendQuery: false,
    });
    setDialog?.(Dialogs.Tags);
    setIsPublishing(false);
  }, [article?.id, navigation, setDialog]);

  const onPublish = useCallback(
    async (publishConfig: PublishContentConfig) => {
      if (!ArticleEntity.getName(article)) {
        onAction(ArticleActions.RenameArticle, { value: EMPTY_ARTICLE_TITLE });
      }
      setIsPublishing(true);
      const result = await fetchIsPublishable();
      const publishable = result?.publishable;
      if (publishable) {
        if (!publishable.isPublishable && publishable.reason === REQUIRED_TAG_ERROR_CODE) {
          handleTagsError();
          return;
        }
      }

      const articleId = ArticleEntity.id(article);
      if (articleId) {
        publishArticle({ input: { articleId, ...publishConfig } })
          .then((res) => {
            const { error } = res;
            if (error) {
              const errorCode = getErrorCode(error);
              if (errorCode === REQUIRED_TAG_ERROR_CODE) {
                handleTagsError();
              }
              return;
            }
            onSuccess(
              routes.ArticlePreviewDevices(articleId),
              'Common:ResourceType.Article',
              publishConfig.note,
            );
          })
          .catch((e) => {
            onError(e);
          })
          .finally(() => {
            setIsPublishing(false);
          });
      }
    },
    [article, fetchIsPublishable, onAction, handleTagsError, publishArticle, onSuccess, onError],
  );

  return { onPublish, fetching: isPublishing };
};

export default useArticlePublish;
